/**
 * Promoted Blueprints Hooks
 * Jotai atoms and hooks for promoted blueprint management
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { useMemo } from 'react';
import { promotedBlueprintService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type { PromotedBlueprintTrack } from '@/modules/promoted-blueprints/types';
import type { TemplateTypeLink } from '@/modules/back-testing/readiness/types';

// Query atoms
// Promoted blueprints live under Training, which is brand-scoped. A brand sees its own
// promoted blueprints AND the account's shared ones — the backend read takes both, so a
// brand that has promoted nothing of its own still shows what the account promoted.
const knowledgeBlueprintsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['promoted-blueprints', 'knowledge', buScopeKey(get)],
  queryFn: () => promotedBlueprintService.listKnowledge(buScopeParams(get))
}));

const workflowBlueprintsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['promoted-blueprints', 'workflow', buScopeKey(get)],
  queryFn: () => promotedBlueprintService.listWorkflow(buScopeParams(get))
}));

const workflowBlueprintByIdQueryAtom = (id: string) =>
  atomWithQuery(() => ({
    queryKey: ['promoted-blueprints', 'workflow', id],
    queryFn: () => promotedBlueprintService.getWorkflowById(id),
    enabled: Boolean(id)
  }));

/** What a blueprint's templates stand on in back-testing; read fresh because a bulk run can change it any time. */
export const templateReadinessKey = (id: string): string[] => ['promoted-blueprints', 'workflow', id, 'template-readiness'];

const templateReadinessQueryAtom = (id: string) =>
  atomWithQuery(() => ({
    queryKey: templateReadinessKey(id),
    queryFn: () => promotedBlueprintService.getTemplateReadiness(id),
    enabled: Boolean(id),
    staleTime: 0
  }));

export const automationKey = (id: string): string[] => ['promoted-blueprints', 'workflow', id, 'automation'];

const automationQueryAtom = (id: string) =>
  atomWithQuery(() => ({
    queryKey: automationKey(id),
    queryFn: () => promotedBlueprintService.getAutomation(id),
    enabled: Boolean(id),
    staleTime: 0
  }));

// Mutation atoms
const setPromotedBlueprintEnabledMutationAtom = atomWithMutation(() => ({
  mutationKey: ['promoted-blueprints', 'set-enabled'],
  mutationFn: ({ id, track, enabled }: { id: string; track: PromotedBlueprintTrack; enabled: boolean }) =>
    promotedBlueprintService.setEnabled(id, track, enabled),
  onSuccess: (_data, variables) => {
    invalidateQueries(['promoted-blueprints', variables.track]);
  }
}));

const setKnowledgeIntentEnabledMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['promoted-blueprints', 'set-knowledge-intent-enabled'],
  mutationFn: ({ intent, enabled }: { intent: string; enabled: boolean }) =>
    promotedBlueprintService.setKnowledgeIntentEnabled(intent, enabled, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['promoted-blueprints', 'knowledge']);
  }
}));

const setPromotedAutoSendMutationAtom = atomWithMutation(() => ({
  mutationKey: ['promoted-blueprints', 'set-auto-send'],
  mutationFn: ({ id, autoSend }: { id: string; autoSend: boolean }) =>
    promotedBlueprintService.setAutoSend(id, autoSend),
  onSuccess: () => {
    invalidateQueries(['promoted-blueprints', 'knowledge']);
  }
}));

const setPromotedTemplateEnabledMutationAtom = atomWithMutation(() => ({
  mutationKey: ['promoted-blueprints', 'set-template-enabled'],
  mutationFn: ({ id, index, enabled }: { id: string; index: number; enabled: boolean }) =>
    promotedBlueprintService.setTemplateEnabled(id, index, enabled),
  onSuccess: (_data, variables) => {
    invalidateQueries(['promoted-blueprints', 'workflow']);
    invalidateQueries(['promoted-blueprints', 'workflow', variables.id]);
  }
}));

const setTemplateTypeLinkMutationAtom = atomWithMutation(() => ({
  mutationKey: ['promoted-blueprints', 'set-template-type-link'],
  mutationFn: ({ id, index, link }: { id: string; index: number; link: TemplateTypeLink | null }) =>
    promotedBlueprintService.setTemplateTypeLink(id, index, link),
  onSuccess: (_data, variables) => {
    invalidateQueries(['promoted-blueprints', 'workflow']);
    invalidateQueries(templateReadinessKey(variables.id));
  }
}));

const applyReadinessMutationAtom = atomWithMutation(() => ({
  mutationKey: ['promoted-blueprints', 'apply-readiness'],
  mutationFn: ({ id }: { id: string }) => promotedBlueprintService.applyReadiness(id),
  onSuccess: (_data, variables) => {
    invalidateQueries(['promoted-blueprints', 'workflow']);
    invalidateQueries(templateReadinessKey(variables.id));
  }
}));

const setAutomationMutationAtom = atomWithMutation(() => ({
  mutationKey: ['promoted-blueprints', 'set-automation'],
  mutationFn: ({ id, stepIds }: { id: string; stepIds: string[] }) =>
    promotedBlueprintService.setAutomation(id, stepIds),
  onSuccess: (_data, variables) => {
    invalidateQueries(automationKey(variables.id));
  }
}));

// Hooks
export function useKnowledgeBlueprints() {
  const [query] = useAtom(knowledgeBlueprintsQueryAtom);
  return query;
}

export function useWorkflowBlueprints() {
  const [query] = useAtom(workflowBlueprintsQueryAtom);
  return query;
}

export function useWorkflowBlueprintById(id?: string) {
  const atom = useMemo(() => workflowBlueprintByIdQueryAtom(id as string), [id]);
  const [query] = useAtom(atom);

  if (!id) {
    return {
      data: undefined,
      isLoading: false,
      isError: false,
      isIdle: true
    } as const;
  }

  return query;
}

export function useSetPromotedBlueprintEnabled() {
  const [mutation] = useAtom(setPromotedBlueprintEnabledMutationAtom);
  return mutation;
}

export function useSetKnowledgeIntentEnabled() {
  const [mutation] = useAtom(setKnowledgeIntentEnabledMutationAtom);
  return mutation;
}

export function useSetPromotedAutoSend() {
  const [mutation] = useAtom(setPromotedAutoSendMutationAtom);
  return mutation;
}

export function useSetPromotedTemplateEnabled() {
  const [mutation] = useAtom(setPromotedTemplateEnabledMutationAtom);
  return mutation;
}

export function useTemplateReadiness(id?: string) {
  const atom = useMemo(() => templateReadinessQueryAtom(id as string), [id]);
  const [query] = useAtom(atom);
  return query;
}

export function useSetTemplateTypeLink() {
  const [mutation] = useAtom(setTemplateTypeLinkMutationAtom);
  return mutation;
}

export function useApplyReadiness() {
  const [mutation] = useAtom(applyReadinessMutationAtom);
  return mutation;
}

export function useBlueprintAutomation(id?: string) {
  const atom = useMemo(() => automationQueryAtom(id as string), [id]);
  const [query] = useAtom(atom);
  return query;
}

export function useSetAutomation() {
  const [mutation] = useAtom(setAutomationMutationAtom);
  return mutation;
}
